import React, { useRef } from "react";
import styles from "./file-input.module.scss";
import { FieldError } from "react-hook-form";
import upload_icon from "../../assets/upload_icon.svg"; 

type FileUploadInputProps = {
  header: string;
  placeholder: string;
  accept?: string;
  supportedFormats?: string;
  maxSizeMB?: number;
  value?: File | null;
  onChange: (file: File | null) => void;
  error?: FieldError | string;
};

const FileUploadInput = ({
  header,
  placeholder,
  accept = ".jpg,.jpeg,.png,.pdf",
  supportedFormats = "JPEG, PNG & PDF",
  maxSizeMB = 10,
  value,
  onChange,
  error,
}: FileUploadInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const errorMessage = typeof error === "string" ? error : error?.message;

  const handleClick = () => inputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      onChange(null);
      return;
    }

    onChange(file);
    // reset so same file can be re-selected if cleared
    e.target.value = "";
  };

  return (
    <div className={styles.container}>
      <span className={styles.header}>{header}</span>

      <div
        className={`${styles["upload-box"]} ${errorMessage ? styles["upload-box--error"] : ""}`}
        onClick={handleClick}
        role="button"
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className={styles["hidden-input"]}
          onChange={handleChange}
        />

        {value ? (
          <div className={styles["file-selected"]}>
            <span className={styles["file-name"]}>{value.name}</span>
            <button
              type="button"
              className={styles["clear-btn"]}
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div className={styles.placeholder}>
            <img src={upload_icon} alt="" className={styles["upload-icon"]} />
            <span className={styles["placeholder-txt"]}>{placeholder}</span>
          </div>
        )}
      </div>

      <span className={styles["format-txt"]}>
        Supported format: {supportedFormats} ({maxSizeMB}MB)
      </span>

      {errorMessage && (
        <span className={styles["error-txt"]}>{errorMessage}</span>
      )}
    </div>
  );
};

export default FileUploadInput;